(() => {
  if (window.pdlVehicleCompatibility) return;
  const requests = new Map();
  const collator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });
  const uniqueSorted = items => [...new Set(items.filter(Boolean))].sort(collator.compare);
  const normalize = value => String(value || '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  function roofTypeForEntry(entry) {
    if (['none', 'integrated', 'raised'].includes(entry.roofType)) return entry.roofType;
    if (entry.hasRail === false) return 'none';
    const description = normalize(`${entry.roof || ''} ${entry.description || ''} ${entry.line || ''}`);
    const support = (entry.codes || []).map(code => String(code || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')).find(code => /^(710[4-7]|720[4-7]|754|753|751|757|775)$/.test(code)) || '';
    if (/longarinas? integrad|flush rail|rente ao teto/.test(description)) return 'integrated';
    if (/longarinas? elevad|raised rail|longarinas? aberta/.test(description)) return 'raised';
    if (/^(7106|7206)$/.test(support)) return 'integrated';
    return 'raised';
  }
  function yearsForEntries(entries, maxYear = new Date().getFullYear()) {
    const years = new Set();
    entries.forEach(entry => {
      const first = Number(entry.startYear);
      const last = Math.min(Number(entry.endYear), Number(maxYear) || new Date().getFullYear());
      for (let year = first; year <= last; year++) years.add(year);
    });
    return [...years].sort((a, b) => b - a);
  }
  function cached(key, request) {
    if (!requests.has(key)) requests.set(key, request().catch(error => { requests.delete(key); throw error; }));
    return requests.get(key);
  }
  function loadLonglife(url) {
    return cached(url, async () => {
      const response = await fetch(url, { headers: { Accept: 'text/plain' } });
      if (!response.ok) throw new Error('Falha ao carregar a tabela Long Life.');
      const payload = (await response.text()).replace(/\s+/g, '');
      const bytes = Uint8Array.from(atob(payload), character => character.charCodeAt(0));
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
      return new Response(stream).json();
    });
  }
  function loadVehicles({ compatibilityUrl, longlifeUrl }) {
    return cached(`${compatibilityUrl}|${longlifeUrl}`, async () => {
      const [thuleData, longlifeDatabase] = await Promise.all([
        cached(compatibilityUrl, async () => {
          const response = await fetch(compatibilityUrl);
          if (!response.ok) throw new Error('Falha ao carregar a tabela Thule.');
          return response.json();
        }),
        loadLonglife(longlifeUrl).catch(() => ({ vehicleEntries: [] }))
      ]);
      const thuleEntries = (thuleData.entries || []).filter(entry => entry.maker && entry.model && entry.startYear && entry.endYear && Array.isArray(entry.codes));
      return { thuleEntries, longlifeDatabase, entries: [...thuleEntries, ...(longlifeDatabase.vehicleEntries || [])] };
    });
  }

  function buildVehicleModels(entries, maxYear) {
    const groups = new Map();
    entries.forEach(entry => {
      const key = JSON.stringify([entry.maker, entry.model]);
      if (!groups.has(key)) groups.set(key, { maker: entry.maker, model: entry.model, entries: [] });
      groups.get(key).entries.push(entry);
    });
    return [...groups.values()].map(car => ({ ...car, years: yearsForEntries(car.entries, maxYear).map(String) }));
  }
  const searchNormalize = text => normalize(text).replace(/[^a-z0-9]+/g, ' ').trim();
  function matchVehicleModels(models, query) {
    const year = query.match(/\b(?:19|20)\d{2}\b/)?.[0] || '';
    const term = searchNormalize(query.replace(/\b(?:19|20)\d{2}\b/g, ''));
    const words = term.split(' ').filter(Boolean);
    if (!words.length) return [];
    return models.filter(car => words.every(word => searchNormalize(car.maker + ' ' + car.model).split(' ').some(part => part.startsWith(word)))
      && (!year || car.years.includes(year))).sort((a, b) =>
        Number(searchNormalize(b.model) === term) - Number(searchNormalize(a.model) === term) || collator.compare(a.maker + a.model, b.maker + b.model)
      ).slice(0, 30).map(car => ({ ...car, year }));
  }
  function mountQuickSearch(root) {
    const form = root.querySelector('[data-vehicle-quick-form]');
    if (!form || form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';
    const input = form.querySelector('[data-vehicle-query]');
    const list = form.querySelector('[data-vehicle-options]');
    const help = form.querySelector('[data-vehicle-help]');
    const yearWrap = form.querySelector('[data-vehicle-year-field]');
    const year = form.querySelector('[data-vehicle-year]');
    const roofWrap = form.querySelector('[data-vehicle-roof-field]');
    const roof = form.querySelector('[data-vehicle-roof]');
    const submit = form.querySelector('[data-vehicle-submit]');
    const maker = form.elements.namedItem('maker');
    const model = form.elements.namedItem('model');
    const text = JSON.parse(form.querySelector('[data-vehicle-text]').textContent);
    let cars = [], matches = [], selected = null, active = -1, loading = false, loaded = false;
    const options = (select, values, placeholder, labels = {}) => {
      select.replaceChildren(new Option(placeholder, ''), ...values.map(value => new Option(labels[value] || value, value)));
      select.disabled = !values.length;
    };
    const close = () => {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    };
    const reset = () => {
      selected = null;
      maker.value = model.value = '';
      options(year, [], text.yearPlaceholder);
      options(roof, [], text.roofPlaceholder);
      yearWrap.hidden = roofWrap.hidden = true;
      submit.disabled = true;
    };
    const updateReady = () => {
      submit.disabled = !(selected && year.value && roof.value);
      if (!submit.disabled) help.textContent = text.ready;
    };
    function updateRoofs() {
      const types = selected && year.value
        ? uniqueSorted(selected.entries.filter(entry => Number(year.value) >= Number(entry.startYear) && Number(year.value) <= Number(entry.endYear)).map(roofTypeForEntry))
        : [];
      options(roof, types, text.roofPlaceholder, { none: text.roofNone, raised: text.roofRaised, integrated: text.roofIntegrated });
      roofWrap.hidden = types.length <= 1;
      if (types.length === 1) roof.value = types[0];
      help.textContent = year.value ? text.chooseRoof : text.chooseYear;
      updateReady();
    }
    function choose(index) {
      const car = matches[index];
      if (!car) return;
      reset();
      selected = car;
      maker.value = car.maker; model.value = car.model;
      input.value = [car.maker, car.model, car.year].filter(Boolean).join(' ');
      options(year, car.years, text.yearPlaceholder);
      yearWrap.hidden = !!car.year;
      year.value = car.year;
      close();
      updateRoofs();
      if (!car.year) year.focus({ preventScroll: true });
      else if (!roofWrap.hidden) roof.focus({ preventScroll: true });
    }
    function show() {
      matches = matchVehicleModels(cars, input.value);
      list.replaceChildren();
      active = -1;
      input.removeAttribute('aria-activedescendant');
      matches.forEach((car, index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.id = list.id + '-' + index;
        button.setAttribute('role', 'option'); button.setAttribute('aria-selected', 'false');
        button.dataset.vehicleChoice = index;
        button.textContent = [car.maker, car.model, car.year].filter(Boolean).join(' ');
        list.append(button);
      });
      list.hidden = !matches.length;
      input.setAttribute('aria-expanded', String(!!matches.length));
      help.textContent = !input.value.trim() ? text.hint : matches.length ? text.chooseVehicle : text.noMatches;
    }
    async function load() {
      if (loaded || loading) return;
      loading = true; help.textContent = text.loading;
      try {
        const data = await loadVehicles(root.dataset);
        if (!data.entries.length) throw new Error('No vehicles');
        cars = buildVehicleModels(data.entries, root.dataset.maxYear);
        loaded = true; input.disabled = false; help.textContent = text.hint;
        if (input.value.trim()) show();
      } catch {
        input.disabled = false;
        help.textContent = text.error;
      } finally { loading = false; }
    }
    input.addEventListener('input', () => { reset(); if (loaded) show(); else load(); });
    input.addEventListener('focus', () => { if (!loaded) load(); else if (!selected) show(); });
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key === 'Enter' && !selected) {
        event.preventDefault();
        if (list.hidden) show();
        if (matches.length) choose(Math.max(0, active));
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      if (list.hidden) show();
      if (!matches.length) return;
      active = active < 0 ? (event.key === 'ArrowDown' ? 0 : matches.length - 1) : (active + (event.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length;
      [...list.children].forEach((button, index) => button.setAttribute('aria-selected', String(index === active)));
      input.setAttribute('aria-activedescendant', list.children[active].id);
      list.children[active].scrollIntoView({ block: 'nearest' });
    });
    list.addEventListener('click', event => {
      const choice = event.target.closest('[data-vehicle-choice]');
      if (choice) choose(Number(choice.dataset.vehicleChoice));
    });
    document.addEventListener('click', event => { if (!form.contains(event.target)) close(); });
    form.addEventListener('focusout', event => {
      if (!form.contains(event.relatedTarget)) close();
    });
    year.addEventListener('change', () => {
      input.value = [selected?.maker, selected?.model, year.value].filter(Boolean).join(' ');
      updateRoofs();
    });
    roof.addEventListener('change', updateReady);
    form.addEventListener('submit', event => {
      if (submit.disabled) { event.preventDefault(); return; }
      close();
      const preview = new URL(location.href).searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role === 'unpublished' ? window.Shopify.theme.id : '');
      const field = form.querySelector('[name="preview_theme_id"]');
      field.value = preview || ''; field.disabled = !preview;
    });
    reset(); load();
  }

  window.pdlVehicleCompatibility = { buildVehicleModels, matchVehicleModels, mountQuickSearch, loadVehicles, loadLonglife, roofTypeForEntry, yearsForEntries, uniqueSorted };
})();
